import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi } from '../../api';
import { onLabBookingConfirmed } from './bookingUtils';

// ─── Inline SVG Icons ─────────────────────────────────────────────────────────

function IconFlask({ color, size = 20 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6" /><path d="M10 3v6l-4 9h12l-4-9V3" />
      <circle cx="9.5" cy="15.5" r="0.8" fill={color} stroke="none" />
    </svg>
  );
}

function IconCheck({ color, size = 16 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function IconArrowRight({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
    </svg>
  );
}

function IconArrowLeft({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

function IconHome({ color, size = 16 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function IconBuilding({ color, size = 16 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
      <path d="M13 7h2" /><path d="M13 11h2" /><path d="M13 15h2" />
      <path d="M5 7h2" /><path d="M5 11h2" /><path d="M5 15h2" />
    </svg>
  );
}

function IconCalendar({ color }: { color: string }) {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M8 2v4" /><path d="M16 2v4" /><path d="M3 10h18" />
    </svg>
  );
}

function IconClock({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function IconUser({ color }: { color: string }) {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconShield({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconSuccess({ color }: { color: string }) {
  return (
    <svg width={52} height={52} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  );
}

function IconInfo({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" /><path d="M12 8h.01" />
    </svg>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Slot {
  id: string;
  time: string;
  date?: string;
  available?: boolean;
}

interface BookingResult {
  sampleCode?: string;
  bookingId?: string;
  confirmationCode?: string;
  id?: string;
}

type CollectionType = 'lab' | 'home';

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({ step, total }: { step: number; total: number }) {
  const steps = ['Review', 'Schedule', 'Details', 'Confirm'];
  return (
    <div style={{ padding: '16px 20px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {steps.map((label, i) => {
          const idx = i + 1;
          const isActive = idx === step;
          const isDone = idx < step;
          return (
            <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, position: 'relative' }}>
              {/* Line before */}
              {i > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 14,
                    width: '50%',
                    height: 2,
                    background: isDone || isActive ? T.teal : T.border,
                    transform: 'translateX(-50%)',
                    zIndex: 0,
                  }}
                />
              )}
              {/* Line after */}
              {i < total - 1 && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 14,
                    width: '50%',
                    height: 2,
                    background: isDone ? T.teal : T.border,
                    transform: 'translateX(50%)',
                    zIndex: 0,
                  }}
                />
              )}
              {/* Circle */}
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: isDone ? T.teal : isActive ? T.navy : T.white,
                  border: `2px solid ${isDone || isActive ? (isDone ? T.teal : T.navy) : T.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  position: 'relative',
                  transition: 'background 0.2s ease, border-color 0.2s ease',
                  flexShrink: 0,
                }}
              >
                {isDone ? (
                  <IconCheck color={T.white} size={12} />
                ) : (
                  <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 800, color: isActive ? T.white : T.textTer }}>
                    {idx}
                  </span>
                )}
              </div>
              <span
                style={{
                  fontFamily: T.font,
                  fontSize: 9,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? T.navy : isDone ? T.teal : T.textTer,
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────

function SectionCard({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: 16,
        overflow: 'hidden',
        marginBottom: 14,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${T.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: T.bg,
        }}
      >
        {icon && <div style={{ display: 'flex', alignItems: 'center' }}>{icon}</div>}
        <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.text, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title}
        </span>
      </div>
      <div style={{ padding: '14px 16px' }}>
        {children}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LabBooking() {
  const { labId = '' } = useParams<{ labId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Parse URL params ──────────────────────────────────────────
  const testIdsFromUrl = (searchParams.get('tests') || '').split(',').filter(Boolean);
  const packageIdsFromUrl = (searchParams.get('packages') || '').split(',').filter(Boolean);

  // ── State ─────────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [labName, setLabName] = useState('');

  // Step 1 - selected items
  const [selectedTestIds, setSelectedTestIds] = useState<string[]>(testIdsFromUrl);
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>(packageIdsFromUrl);
  const [tests, setTests] = useState<{ id: string; name: string; price: number; specimen?: string; tat?: string }[]>([]);
  const [packages, setPackages] = useState<{ id: string; name: string; price: number; discountedPrice?: number }[]>([]);
  const [loadingItems, setLoadingItems] = useState(true);

  // Step 2 - schedule
  const [collectionType, setCollectionType] = useState<CollectionType>('lab');
  const [homeAddress, setHomeAddress] = useState('');
  const [selectedDateOffset, setSelectedDateOffset] = useState(0);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState('');

  // Step 3 - patient details
  const [phone, setPhone] = useState('');
  // The REAL card id from the profile — never a placeholder literal.
  const [nhcId, setNhcId] = useState('');
  useEffect(() => {
    let alive = true;
    patientApi.getProfile()
      .then((p) => { if (alive) setNhcId(p.nhcId || ''); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const [consentGranted, setConsentGranted] = useState(false);

  // Step 4 / submit
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Date helpers ──────────────────────────────────────────────
  const getDateForOffset = (offset: number): Date => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return d;
  };

  const formatDate = (d: Date) => {
    const [y, mo, da] = [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')];
    return `${y}-${mo}-${da}`;
  };

  const formatDateLabel = (offset: number): string => {
    if (offset === 0) return 'Today';
    if (offset === 1) return 'Tomorrow';
    const d = getDateForOffset(offset);
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  // ── Load lab + tests/packages ──────────────────────────────────
  useEffect(() => {
    let alive = true;
    if (!labId) return;
    const load = async () => {
      setLoadingItems(true);
      // The lab's own profile is real; a per-lab test/package catalog isn't
      // a feature yet, so these three calls are independent — one 404 must
      // never hide the other two succeeding.
      const profileRes = await patientApi.lab.getLabProfile(labId).catch(() => null);
      if (alive) {
        const profile = (profileRes as any)?.data ?? profileRes ?? {};
        setLabName(profile?.labName || profile?.name || 'Lab');
      }

      const testsRes = await patientApi.lab.getLabTests(labId).catch(() => null);
      if (alive) {
        const testsData = (testsRes as any)?.data ?? testsRes ?? [];
        const testList = Array.isArray(testsData) ? testsData : testsData.tests ?? [];
        setTests(testList);
      }

      const pkgsRes = await patientApi.lab.getLabPackages(labId).catch(() => null);
      if (alive) {
        const pkgsData = (pkgsRes as any)?.data ?? pkgsRes ?? [];
        const pkgList = Array.isArray(pkgsData) ? pkgsData : pkgsData.packages ?? [];
        setPackages(pkgList);
      }

      if (alive) setLoadingItems(false);
    };
    load();
    return () => { alive = false; };
  }, [labId]);

  // ── Load slots when date/step changes ─────────────────────────
  useEffect(() => {
    let alive = true;
    if (step !== 2) return;
    const loadSlots = async () => {
      setLoadingSlots(true);
      setSelectedSlotId('');
      const dateStr = formatDate(getDateForOffset(selectedDateOffset));
      try {
        const res = await patientApi.lab.getLabSlots(labId, { date: dateStr });
        const data = (res as any)?.data ?? res ?? [];
        const slotList: Slot[] = Array.isArray(data) ? data : data.slots ?? [];
        if (alive) setSlots(slotList);
      } catch {
        if (alive) setSlots([]);
      } finally {
        if (alive) setLoadingSlots(false);
      }
    };
    loadSlots();
    return () => { alive = false; };
  }, [step, selectedDateOffset, labId]);

  // ── Derived cart values ───────────────────────────────────────
  const selectedTests = tests.filter((t) => selectedTestIds.includes(t.id));
  const selectedPackages = packages.filter((p) => selectedPackageIds.includes(p.id));

  const totalPrice =
    selectedTests.reduce((s, t) => s + (t.price || 0), 0) +
    selectedPackages.reduce((s, p) => s + (p.discountedPrice ?? p.price ?? 0), 0);

  const totalItems = selectedTests.length + selectedPackages.length;

  const toggleTest = (id: string) => {
    setSelectedTestIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const togglePackage = (id: string) => {
    setSelectedPackageIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  // ── Submit booking ────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    const dateStr = formatDate(getDateForOffset(selectedDateOffset));

    try {
      const res = await patientApi.lab.bookAppointment({
        labProfileId: labId,
        testIds: selectedTestIds,
        packageIds: selectedPackageIds,
        collectionType: collectionType === 'home' ? 'HOME_COLLECTION' : 'LAB_VISIT',
        homeAddress: collectionType === 'home' ? homeAddress : undefined,
        slotDate: dateStr,
        slotId: selectedSlotId || undefined,
        phone,
        consentGranted,
      });
      const apiResult: BookingResult = (res as any)?.data ?? res ?? {};

      const selectedSlot = slots.find((s) => s.id === selectedSlotId);
      const testNames = selectedTests.map(t => t.name).join(', ');
      try {
        onLabBookingConfirmed({
          id: apiResult.id || apiResult.bookingId,
          testName: testNames || 'Lab Tests',
          labName: labName || 'Lab',
          date: dateStr,
          time: selectedSlot?.time || dateStr,
          collectionType: collectionType === 'home' ? 'Home Collection' : 'Walk-in',
        });
      } catch { /* non-blocking */ }

      setBookingResult(apiResult);
    } catch (err: any) {
      setSubmitError(err?.message || 'Could not complete booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedStep1 = totalItems > 0;
  const isAddressValid = collectionType === 'lab' || homeAddress.trim().length > 5;
  const canProceedStep2 = (slots.length === 0 || selectedSlotId !== '') && isAddressValid;
  const isPhoneValid = /^\d{10}$/.test(phone.trim());
  const canProceedStep3 = isPhoneValid && consentGranted;

  // ── Success Screen ────────────────────────────────────────────
  if (bookingResult) {
    // Only server-issued references are shown; absent → honest placeholder.
    const code = bookingResult.sampleCode || bookingResult.confirmationCode || bookingResult.bookingId || bookingResult.id || '—';
    return (
      <div style={{ background: T.bg, fontFamily: T.font, maxWidth: 1080, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', paddingBottom: 100 }}>
        <div
          style={{
            background: T.white,
            border: `1px solid ${T.border}`,
            borderRadius: 24,
            padding: '40px 28px',
            textAlign: 'center',
            width: '100%',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}
        >
          {/* Success icon */}
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: '#059669' + '14',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <IconSuccess color="#059669" />
          </div>

          <h1 style={{ fontFamily: T.font, fontSize: 20, fontWeight: 800, color: T.text, margin: '0 0 8px' }}>
            Booking Confirmed!
          </h1>
          <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec, margin: '0 0 24px', lineHeight: 1.6 }}>
            Your lab appointment has been booked successfully with <strong>{labName}</strong>.
          </p>

          {/* Code pill */}
          <div
            style={{
              background: T.teal + '12',
              border: `1.5px dashed ${T.teal}50`,
              borderRadius: 12,
              padding: '14px 20px',
              marginBottom: 28,
            }}
          >
            <div style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: T.textTer, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
              Sample / Booking Code
            </div>
            <div style={{ fontFamily: T.font, fontSize: 18, fontWeight: 800, color: T.teal, letterSpacing: '0.06em' }}>
              {code}
            </div>
          </div>

          {/* Summary recap */}
          <div
            style={{
              background: T.bg,
              borderRadius: 12,
              padding: '14px 16px',
              textAlign: 'left',
              marginBottom: 28,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <SummaryRow label="Lab" value={labName} />
            <SummaryRow label="Collection" value={collectionType === 'home' ? 'Home Collection' : 'Lab Visit'} />
            <SummaryRow label="Date" value={formatDateLabel(selectedDateOffset)} />
            {selectedSlotId && slots.find((s) => s.id === selectedSlotId) && (
              <SummaryRow label="Time" value={slots.find((s) => s.id === selectedSlotId)!.time} />
            )}
            <SummaryRow label="Total (pay at lab)" value={`₹${totalPrice.toLocaleString('en-IN')}`} bold />
          </div>

          {/* NeoHealthCard branding */}
          <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6, display: 'block', marginBottom: 16 }}>
            NeoHealthCard™ · Booking synced with your NeoHealthCard account · Every access is audit logged
          </span>

          {/* Action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={() => navigate('/patient/lab-appointments')}
              style={{
                background: `linear-gradient(135deg, ${T.teal} 0%, #00A8A8 100%)`,
                border: 'none',
                borderRadius: 12,
                padding: '14px 20px',
                fontFamily: T.font,
                fontSize: 14,
                fontWeight: 700,
                color: T.white,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              View Lab Orders
              <IconArrowRight color={T.white} />
            </button>
            <button
              onClick={() => navigate('/patient/lab-reports')}
              style={{
                background: T.white,
                border: `1.5px solid ${T.teal}`,
                borderRadius: 12,
                padding: '13px 20px',
                fontFamily: T.font,
                fontSize: 13,
                fontWeight: 700,
                color: T.teal,
                cursor: 'pointer',
              }}
            >
              View Lab Reports
            </button>
            <button
              onClick={() => navigate('/patient/timeline')}
              style={{
                background: T.white,
                border: `1.5px solid ${T.navy}`,
                borderRadius: 12,
                padding: '13px 20px',
                fontFamily: T.font,
                fontSize: 13,
                fontWeight: 700,
                color: T.navy,
                cursor: 'pointer',
              }}
            >
              Go to Timeline
            </button>
            <button
              onClick={() => navigate('/patient/care-services')}
              style={{
                background: T.white,
                border: `1.5px solid ${T.border}`,
                borderRadius: 12,
                padding: '13px 20px',
                fontFamily: T.font,
                fontSize: 13,
                fontWeight: 600,
                color: T.textSec,
                cursor: 'pointer',
              }}
            >
              Back to Care Services
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: T.bg, fontFamily: T.font, maxWidth: 1080, margin: '0 auto', paddingBottom: 100 }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .booking-input {
          width: 100%;
          font-family: inherit;
          font-size: 13px;
          color: ${T.text};
          background: ${T.bg};
          border: 1.5px solid ${T.border};
          border-radius: 10px;
          padding: 11px 14px;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.15s ease;
        }
        .booking-input:focus { border-color: ${T.teal}; background: white; }
      `}</style>

      {/* ── Header ──────────────────────────────────────────────── */}
      <div
        style={{
          background: `linear-gradient(145deg, ${T.navy} 0%, #004080 60%, ${T.teal} 100%)`,
          padding: '40px 20px 20px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconFlask color="white" size={20} />
          </div>
          <div>
            <h1 style={{ fontFamily: T.font, fontSize: 18, fontWeight: 800, color: T.white, margin: 0 }}>Book Lab Test</h1>
            <p style={{ fontFamily: T.font, fontSize: 11, color: 'rgba(255,255,255,0.7)', margin: 0, marginTop: 2 }}>
              {labName || 'Loading…'}
            </p>
          </div>
        </div>
      </div>

      {/* Step indicator */}
      <StepIndicator step={step} total={4} />

      <div style={{ padding: '20px 16px 0', animation: 'fadeIn 0.2s ease' }}>

        {/* ════════════════════════════════════════════════════════════ */}
        {/* STEP 1: Review Selection */}
        {/* ════════════════════════════════════════════════════════════ */}
        {step === 1 && (
          <>
            <SectionCard title="Selected Tests" icon={<IconFlask color={T.teal} size={14} />}>
              {loadingItems ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ width: 20, height: 20, border: `2px solid ${T.border}`, borderTopColor: T.teal, borderRadius: '50%', animation: 'spin 0.6s linear infinite', margin: '0 auto' }} />
                </div>
              ) : tests.length === 0 && packages.length === 0 ? (
                <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: 0, textAlign: 'center', padding: '10px 0' }}>
                  No tests or packages loaded. Add items from the lab store.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {tests.length > 0 && (
                    <>
                      <div style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: T.textTer, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                        Individual Tests
                      </div>
                      {tests.map((t) => (
                        <SelectionRow
                          key={t.id}
                          name={t.name}
                          price={t.price}
                          selected={selectedTestIds.includes(t.id)}
                          onToggle={() => toggleTest(t.id)}
                          accent={T.teal}
                        />
                      ))}
                    </>
                  )}
                  {packages.length > 0 && (
                    <>
                      <div style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: T.textTer, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: tests.length > 0 ? 8 : 0, marginBottom: 2 }}>
                        Packages
                      </div>
                      {packages.map((p) => (
                        <SelectionRow
                          key={p.id}
                          name={p.name}
                          price={p.discountedPrice ?? p.price}
                          originalPrice={p.discountedPrice != null ? p.price : undefined}
                          selected={selectedPackageIds.includes(p.id)}
                          onToggle={() => togglePackage(p.id)}
                          accent={T.purple}
                        />
                      ))}
                    </>
                  )}
                </div>
              )}
            </SectionCard>

            {/* Price summary */}
            {totalItems > 0 && (
              <div
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 16,
                  padding: '14px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 14,
                }}
              >
                <div>
                  <div style={{ fontFamily: T.font, fontSize: 11, color: T.textSec }}>
                    {totalItems} item{totalItems !== 1 ? 's' : ''} selected
                  </div>
                  <div style={{ fontFamily: T.font, fontSize: 18, fontWeight: 800, color: T.navy }}>
                    ₹{totalPrice.toLocaleString('en-IN')}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/patient/care-services/labs/${labId}`)}
                  style={{
                    background: 'none',
                    border: `1.5px solid ${T.teal}`,
                    borderRadius: 10,
                    padding: '7px 14px',
                    fontFamily: T.font,
                    fontSize: 12,
                    fontWeight: 700,
                    color: T.teal,
                    cursor: 'pointer',
                  }}
                >
                  + Add more
                </button>
              </div>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════════ */}
        {/* STEP 2: Collection Type + Schedule */}
        {/* ════════════════════════════════════════════════════════════ */}
        {step === 2 && (
          <>
            {/* Collection type */}
            <SectionCard title="Collection Type" icon={<IconHome color={T.navy} size={14} />}>
              <div style={{ display: 'flex', gap: 10 }}>
                {([
                  { type: 'lab' as CollectionType, label: 'Lab Visit', sub: 'Visit the lab for sample collection', icon: <IconBuilding color={collectionType === 'lab' ? T.navy : T.textSec} size={20} /> },
                  { type: 'home' as CollectionType, label: 'Home Collection', sub: 'Phlebotomist visits your home', icon: <IconHome color={collectionType === 'home' ? T.teal : T.textSec} size={20} /> },
                ] as { type: CollectionType; label: string; sub: string; icon: React.ReactNode }[]).map(({ type, label, sub, icon }) => {
                  const active = collectionType === type;
                  const accent = type === 'lab' ? T.navy : T.teal;
                  return (
                    <button
                      key={type}
                      onClick={() => setCollectionType(type)}
                      style={{
                        flex: 1,
                        background: active ? accent + '0f' : T.white,
                        border: `2px solid ${active ? accent : T.border}`,
                        borderRadius: 14,
                        padding: '14px 12px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        transition: 'border-color 0.12s ease, background 0.12s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {icon}
                        {active && (
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <IconCheck color={T.white} size={10} />
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: active ? accent : T.text }}>{label}</div>
                        <div style={{ fontFamily: T.font, fontSize: 10, color: T.textSec, lineHeight: 1.4, marginTop: 2 }}>{sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Home address textarea */}
              {collectionType === 'home' && (
                <div style={{ marginTop: 14 }}>
                  <label style={{ fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.textSec, display: 'block', marginBottom: 6 }}>
                    Collection Address *
                  </label>
                  <textarea
                    className="booking-input"
                    rows={3}
                    placeholder="Enter your full address for sample pickup…"
                    value={homeAddress}
                    onChange={(e) => setHomeAddress(e.target.value)}
                    style={{
                      width: '100%',
                      fontFamily: T.font,
                      fontSize: 13,
                      color: T.text,
                      background: T.bg,
                      border: `1.5px solid ${homeAddress.trim().length > 5 ? T.teal : homeAddress.length > 0 ? '#DC2626' : T.border}`,
                      borderRadius: 10,
                      padding: '11px 14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      resize: 'none',
                      lineHeight: 1.5,
                    }}
                  />
                  {homeAddress.length > 0 && homeAddress.trim().length <= 5 && (
                    <span style={{ fontFamily: T.font, fontSize: 10, color: '#DC2626', marginTop: 4, display: 'block' }}>
                      Please enter a complete address for home collection
                    </span>
                  )}
                  {collectionType === 'home' && homeAddress.trim().length === 0 && (
                    <span style={{ fontFamily: T.font, fontSize: 10, color: T.textTer, marginTop: 4, display: 'block' }}>
                      Address is required for home collection
                    </span>
                  )}
                </div>
              )}
            </SectionCard>

            {/* Date selector */}
            <SectionCard title="Select Date" icon={<IconCalendar color={T.navy} />}>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                {[0, 1, 2, 3, 4].map((offset) => {
                  const active = selectedDateOffset === offset;
                  const d = getDateForOffset(offset);
                  const dayName = offset === 0 ? 'Today' : offset === 1 ? 'Tomorrow' : d.toLocaleDateString('en-IN', { weekday: 'short' });
                  const dayNum = d.getDate();
                  const monthName = d.toLocaleDateString('en-IN', { month: 'short' });
                  return (
                    <button
                      key={offset}
                      onClick={() => setSelectedDateOffset(offset)}
                      style={{
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 3,
                        background: active ? T.navy : T.white,
                        border: `1.5px solid ${active ? T.navy : T.border}`,
                        borderRadius: 12,
                        padding: '10px 14px',
                        cursor: 'pointer',
                        transition: 'background 0.12s ease, border-color 0.12s ease',
                        minWidth: 60,
                      }}
                    >
                      <span style={{ fontFamily: T.font, fontSize: 9, fontWeight: 600, color: active ? 'rgba(255,255,255,0.7)' : T.textTer }}>
                        {dayName}
                      </span>
                      <span style={{ fontFamily: T.font, fontSize: 18, fontWeight: 800, color: active ? T.white : T.text, lineHeight: 1 }}>
                        {dayNum}
                      </span>
                      <span style={{ fontFamily: T.font, fontSize: 9, fontWeight: 500, color: active ? 'rgba(255,255,255,0.7)' : T.textSec }}>
                        {monthName}
                      </span>
                    </button>
                  );
                })}
              </div>
            </SectionCard>

            {/* Time slots */}
            <SectionCard title="Select Time Slot" icon={<IconClock color={T.teal} />}>
              {loadingSlots ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} style={{ height: 34, width: 80, borderRadius: 8, background: T.border, animation: 'pulse 1.4s ease infinite' }} />
                  ))}
                </div>
              ) : slots.length === 0 ? (
                <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: 0 }}>
                  Time slot booking isn't available for this lab yet — the lab will call to confirm a suitable time after you book.
                </p>
              ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {slots.map((slot) => {
                    const active = selectedSlotId === slot.id;
                    const available = slot.available !== false;
                    return (
                      <button
                        key={slot.id}
                        onClick={() => available && setSelectedSlotId(slot.id)}
                        disabled={!available}
                        style={{
                          background: active ? T.teal : available ? T.white : T.bg,
                          border: `1.5px solid ${active ? T.teal : available ? T.border : T.border}`,
                          borderRadius: 8,
                          padding: '7px 13px',
                          fontFamily: T.font,
                          fontSize: 12,
                          fontWeight: active ? 700 : 500,
                          color: active ? T.white : available ? T.text : T.textTer,
                          cursor: available ? 'pointer' : 'not-allowed',
                          opacity: available ? 1 : 0.5,
                          transition: 'background 0.1s ease, border-color 0.1s ease',
                        }}
                      >
                        {slot.time}
                      </button>
                    );
                  })}
                </div>
              )}
            </SectionCard>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════ */}
        {/* STEP 3: Patient Details */}
        {/* ════════════════════════════════════════════════════════════ */}
        {step === 3 && (
          <>
            <SectionCard title="Patient Details" icon={<IconUser color={T.navy} />}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* NHC ID */}
                <div>
                  <label style={{ fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.textSec, display: 'block', marginBottom: 6 }}>
                    NHC ID
                  </label>
                  <input
                    className="booking-input"
                    type="text"
                    value={nhcId}
                    readOnly
                    style={{
                      width: '100%',
                      fontFamily: T.font,
                      fontSize: 13,
                      color: T.textSec,
                      background: T.bg,
                      border: `1.5px solid ${T.border}`,
                      borderRadius: 10,
                      padding: '11px 14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      cursor: 'not-allowed',
                    }}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label style={{ fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.textSec, display: 'block', marginBottom: 6 }}>
                    Mobile Number *
                  </label>
                  <input
                    className="booking-input"
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    style={{
                      width: '100%',
                      fontFamily: T.font,
                      fontSize: 13,
                      color: T.text,
                      background: T.bg,
                      border: `1.5px solid ${phone.length === 10 ? T.teal : phone.length > 0 ? '#DC2626' : T.border}`,
                      borderRadius: 10,
                      padding: '11px 14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  {phone.length > 0 && phone.length < 10 && (
                    <span style={{ fontFamily: T.font, fontSize: 10, color: '#DC2626', marginTop: 4, display: 'block' }}>
                      Phone number must be exactly 10 digits ({10 - phone.length} more needed)
                    </span>
                  )}
                </div>

                {/* Info note */}
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    background: T.teal + '0c',
                    border: `1px solid ${T.teal}28`,
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}
                >
                  <IconInfo color={T.teal} />
                  <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: 0, lineHeight: 1.6 }}>
                    Age and gender information will be pulled from your NHC health profile automatically.
                  </p>
                </div>

                {/* Fasting note */}
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    background: '#FEF9C3',
                    border: '1px solid #FDE68A',
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <p style={{ fontFamily: T.font, fontSize: 11, color: '#92400E', margin: 0, lineHeight: 1.6 }}>
                    <strong>Fasting may be required</strong> for certain tests (e.g. blood glucose, lipid panel). Confirm with the lab before your appointment.
                  </p>
                </div>
              </div>
            </SectionCard>

            {/* Consent */}
            <SectionCard title="Consent" icon={<IconShield color={T.navy} />}>
              <button
                onClick={() => setConsentGranted(!consentGranted)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 6,
                    border: `2px solid ${consentGranted ? T.teal : T.border}`,
                    background: consentGranted ? T.teal : T.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'background 0.12s ease, border-color 0.12s ease',
                    marginTop: 1,
                  }}
                >
                  {consentGranted && <IconCheck color={T.white} size={12} />}
                </div>
                <span style={{ fontFamily: T.font, fontSize: 12, color: T.text, lineHeight: 1.6 }}>
                  I consent to share my relevant health records and test history with this lab for the purpose of this booking, in accordance with NeoHealthCard's privacy policy. *
                </span>
              </button>
            </SectionCard>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════ */}
        {/* STEP 4: Confirm */}
        {/* ════════════════════════════════════════════════════════════ */}
        {step === 4 && (
          <>
            {/* Booking summary */}
            <SectionCard title="Booking Summary" icon={<IconFlask color={T.teal} size={14} />}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <SummaryRow label="Lab" value={labName} />
                <SummaryRow label="Collection" value={collectionType === 'home' ? 'Home Collection' : 'Lab Visit'} />
                {collectionType === 'home' && homeAddress && (
                  <SummaryRow label="Address" value={homeAddress} />
                )}
                <SummaryRow label="Date" value={formatDateLabel(selectedDateOffset)} />
                {selectedSlotId && slots.find((s) => s.id === selectedSlotId) && (
                  <SummaryRow label="Time" value={slots.find((s) => s.id === selectedSlotId)!.time} />
                )}
                <div style={{ height: 1, background: T.border, margin: '4px 0' }} />
                {selectedTests.map((t) => (
                  <SummaryRow key={t.id} label={t.name} value={`₹${t.price.toLocaleString('en-IN')}`} />
                ))}
                {selectedPackages.map((p) => (
                  <SummaryRow key={p.id} label={p.name} value={`₹${(p.discountedPrice ?? p.price).toLocaleString('en-IN')}`} />
                ))}
                <div style={{ height: 1, background: T.border, margin: '4px 0' }} />
                <SummaryRow label="Total" value={`₹${totalPrice.toLocaleString('en-IN')}`} bold />
              </div>
            </SectionCard>

            {/* Payment placeholder */}
            <div
              style={{
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: 16,
                padding: '16px',
                marginBottom: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#059669' + '14', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" />
                  <path d="M1 10h22" />
                </svg>
              </div>
              <div>
                <div style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.text }}>Pay at Lab / Cash on Collection</div>
                <div style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, marginTop: 2 }}>Online payment gateway coming soon</div>
              </div>
            </div>

            {/* Error */}
            {submitError && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '12px 16px', marginBottom: 14 }}>
                <p style={{ fontFamily: T.font, fontSize: 12, color: '#DC2626', margin: 0 }}>{submitError}</p>
              </div>
            )}
          </>
        )}

        {/* ── Navigation Buttons ───────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              style={{
                flex: step < 4 ? 0 : 1,
                minWidth: 80,
                background: T.white,
                border: `1.5px solid ${T.border}`,
                borderRadius: 12,
                padding: '14px 16px',
                fontFamily: T.font,
                fontSize: 13,
                fontWeight: 600,
                color: T.textSec,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
              }}
            >
              <IconArrowLeft color={T.textSec} />
              Back
            </button>
          )}

          {step < 4 && (
            <button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && !canProceedStep1) ||
                (step === 2 && !canProceedStep2) ||
                (step === 3 && !canProceedStep3)
              }
              style={{
                flex: 1,
                background: `linear-gradient(135deg, ${T.teal} 0%, #00A8A8 100%)`,
                border: 'none',
                borderRadius: 12,
                padding: '14px 16px',
                fontFamily: T.font,
                fontSize: 14,
                fontWeight: 700,
                color: T.white,
                cursor: (step === 1 && !canProceedStep1) || (step === 2 && !canProceedStep2) || (step === 3 && !canProceedStep3) ? 'not-allowed' : 'pointer',
                opacity: (step === 1 && !canProceedStep1) || (step === 2 && !canProceedStep2) || (step === 3 && !canProceedStep3) ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                boxShadow: `0 3px 12px ${T.teal}40`,
              }}
            >
              {step === 1 ? 'Schedule Appointment' : step === 2 ? 'Enter Details' : 'Review & Confirm'}
              <IconArrowRight color={T.white} />
            </button>
          )}

          {step === 4 && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                flex: 1,
                background: submitting ? T.textTer : `linear-gradient(135deg, ${T.teal} 0%, #00A8A8 100%)`,
                border: 'none',
                borderRadius: 12,
                padding: '14px 16px',
                fontFamily: T.font,
                fontSize: 14,
                fontWeight: 700,
                color: T.white,
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow: submitting ? 'none' : `0 3px 12px ${T.teal}40`,
              }}
            >
              {submitting ? (
                <>
                  <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: T.white, borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                  Confirming…
                </>
              ) : (
                <>
                  Confirm Booking
                  <IconCheck color={T.white} size={16} />
                </>
              )}
            </button>
          )}
        </div>

        {/* Step helper text */}
        {step === 1 && !canProceedStep1 && !loadingItems && (
          <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, textAlign: 'center', marginTop: 8 }}>
            Select at least one test or package to continue.
          </p>
        )}
        {step === 2 && !selectedSlotId && slots.length > 0 && (
          <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, textAlign: 'center', marginTop: 8 }}>
            Please select a time slot to continue.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Helper: Selection Row ────────────────────────────────────────────────────

function SelectionRow({
  name, price = 0, originalPrice, selected, onToggle, accent,
}: {
  name: string;
  price?: number;
  originalPrice?: number;
  selected: boolean;
  onToggle: () => void;
  accent: string;
}) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: selected ? accent + '0c' : T.white,
        border: `1.5px solid ${selected ? accent : T.border}`,
        borderRadius: 10,
        padding: '10px 12px',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: 'background 0.1s ease, border-color 0.1s ease',
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: 5,
          border: `2px solid ${selected ? accent : T.border}`,
          background: selected ? accent : T.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'background 0.1s ease, border-color 0.1s ease',
        }}
      >
        {selected && <IconCheck color={T.white} size={11} />}
      </div>
      <span style={{ flex: 1, fontFamily: T.font, fontSize: 12, fontWeight: 500, color: T.text }}>
        {name}
      </span>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.navy }}>
          ₹{price.toLocaleString('en-IN')}
        </div>
        {originalPrice != null && (
          <div style={{ fontFamily: T.font, fontSize: 10, color: T.textTer, textDecoration: 'line-through' }}>
            ₹{originalPrice.toLocaleString('en-IN')}
          </div>
        )}
      </div>
    </button>
  );
}

// ─── Helper: Summary Row ──────────────────────────────────────────────────────

function SummaryRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, flex: 1 }}>{label}</span>
      <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: bold ? 800 : 600, color: bold ? T.navy : T.text, textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}
